import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type UserRole = "admin" | "user";

// Cache the role to prevent unnecessary refetches
const roleCache: { userId: string | null; role: UserRole | null; timestamp: number } = {
  userId: null,
  role: null,
  timestamp: 0,
};

const CACHE_TTL_MS = 30000; // 30 seconds cache

async function fetchRoleForUser(userId: string, retries = 3): Promise<UserRole> {
  // Check cache first
  const now = Date.now();
  if (
    roleCache.userId === userId &&
    roleCache.role !== null &&
    now - roleCache.timestamp < CACHE_TTL_MS
  ) {
    return roleCache.role;
  }

  let lastError: Error | null = null;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        lastError = error;
        // Wait before retry with exponential backoff
        if (attempt < retries - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100 * Math.pow(2, attempt)));
          continue;
        }
      }

      const role = (data?.role as UserRole) || "user";
      
      // Update cache
      roleCache.userId = userId;
      roleCache.role = role;
      roleCache.timestamp = Date.now();

      return role;
    } catch (e) {
      lastError = e as Error;
      if (attempt < retries - 1) {
        await new Promise((resolve) => setTimeout(resolve, 100 * Math.pow(2, attempt)));
      }
    }
  }

  // If all retries failed, log error and return safe default
  console.error("Error fetching user role after retries:", lastError);
  return "user";
}

// Force clear the role cache (useful when role changes)
export function clearRoleCache() {
  roleCache.userId = null;
  roleCache.role = null;
  roleCache.timestamp = 0;
}

export function useUserRole() {
  const { user, loading: authLoading } = useAuth();
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!user) {
      clearRoleCache();
      setRole(null);
      setLoading(false);
      return;
    }

    try {
      const fetchedRole = await fetchRoleForUser(user.id);
      if (mountedRef.current) {
        setRole(fetchedRole);
      }
    } catch (e) {
      console.error("Error in fetchUserRole:", e);
      if (mountedRef.current) {
        setRole("user");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [user]);

  useEffect(() => {
    mountedRef.current = true;

    if (!authLoading) {
      refresh();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [refresh, authLoading]);

  return { 
    role, 
    userId: user?.id ?? null, 
    loading: authLoading || loading, 
    isAdmin: role === "admin", 
    refresh 
  };
}
